import type { QuizState } from 'room/training-room/redux'
import { UserId } from 'common/types'

export type QuizOptionResult = 'correct' | 'incorrect'

export type QuizOption = {
	id: string
	text: string
	/** I confirmed this answer. */
	mine: boolean
	/** Set once the trainer reveals the answer: the right one, or my wrong one. */
	result?: QuizOptionResult
}

/**
 * Where I stand on the current question:
 * - `answering` - nothing confirmed yet, the question is open;
 * - `waiting` - confirmed, the trainer has not revealed the answer;
 * - `correct` / `wrong` - revealed, and how my answer fared;
 * - `missed` - revealed before I confirmed anything.
 */
export type QuizOutcome =
	| 'answering'
	| 'waiting'
	| 'correct'
	| 'wrong'
	| 'missed'

export type QuizQuestion = {
	/** 1-based, as the quiz counts it. */
	number: number
	total: number
	text: string
	imageId?: string
	options: QuizOption[]
	outcome: QuizOutcome
}

/**
 * The phone's view of a quiz question, from the room's quiz state.
 * `undefined` once the quiz is over and only the ranking is left.
 */
export const quizQuestionOf = (
	quiz: QuizState,
	me: UserId
): QuizQuestion | undefined => {
	if (quiz.phase === 'quiz-concluded') {
		return undefined
	}
	const { state } = quiz
	const selected =
		quiz.phase === 'question-asked' ? {} : quiz.state.selectedAnswers
	const correct =
		quiz.phase === 'question-completed' ? quiz.state.correctAnswers : undefined

	const options = Object.entries(state.answerTexts).map(
		([id, text]): QuizOption => {
			const mine = !!selected[id]?.some(user => user.id === me)
			const result: QuizOptionResult | undefined = !correct
				? undefined
				: correct.includes(id)
				? 'correct'
				: mine
				? 'incorrect'
				: undefined
			return { id, text, mine, result }
		}
	)

	const myOption = options.find(option => option.mine)
	const outcome: QuizOutcome = !correct
		? myOption
			? 'waiting'
			: 'answering'
		: !myOption
		? 'missed'
		: myOption.result === 'correct'
		? 'correct'
		: 'wrong'

	return {
		number: state.number,
		total: state.totalQuestions,
		text: state.text,
		imageId: state.imageId,
		options,
		outcome,
	}
}
